import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { VehicleOwnerStep } from '../_sections/steps/VehicleOwnerStep';
import { VehicleOwnerFormData } from '@app/domain/model/eshop/transport/VehicleOwnerFormData';
import { Address } from '@app/domain/model/Address';
import { useCallback, useState, forwardRef, useImperativeHandle } from 'react';

export interface EditableVehicleOwnerRef {
    getCurrentData: () => VehicleOwnerFormData;
}

interface Props {
    initialData: VehicleOwnerFormData;
    formState: FormState<PurchaseTransportInsuranceResult>;
    isCompany: boolean;
}

export const EditableVehicleOwner = forwardRef<EditableVehicleOwnerRef, Props>(
    ({ initialData, formState, isCompany }, ref) => {
        const [formData, setFormData] = useState<VehicleOwnerFormData>(() => ({
            ...initialData,
            ownerAddress: initialData.ownerAddress ? { ...initialData.ownerAddress } : null,
            payerAddress: initialData.payerAddress ? { ...initialData.payerAddress } : null,
        }));

        useImperativeHandle(
            ref,
            () => ({
                getCurrentData: () => formData,
            }),
            [formData],
        );

        const handleInputField = useCallback((field: keyof VehicleOwnerFormData) => {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                setFormData(prev => ({
                    ...prev,
                    [field]: value,
                }));
            };
        }, []);

        const handleInputChange = useCallback((field: keyof VehicleOwnerFormData, value: string | boolean | null) => {
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }));
        }, []);

        const formatPhoneNumber = useCallback((value: string) => {
            const cleaned = value.replace(/[^\d+]/g, '');

            if (!cleaned) {
                return '+370';
            }

            if (!cleaned.startsWith('+')) {
                return '+' + cleaned;
            }

            if (cleaned.startsWith('+370')) {
                const digits = cleaned.slice(4);
                return '+370' + digits.slice(0, 8);
            }

            return cleaned;
        }, []);

        const handlePhoneNumberField = useCallback(
            (field: 'phoneNumber' | 'payerPhoneNumber') => {
                return (event: React.ChangeEvent<HTMLInputElement>) => {
                    const formattedValue = formatPhoneNumber(event.target.value);
                    handleInputChange(field, formattedValue);
                };
            },
            [formatPhoneNumber, handleInputChange],
        );

        const onChangeOwnerAddress = useCallback((address: Nullable<Address>) => {
            setFormData(prev => ({
                ...prev,
                ownerAddress: address,
            }));
        }, []);

        const onChangePayerAddress = useCallback((address: Nullable<Address>) => {
            setFormData(prev => ({
                ...prev,
                payerAddress: address,
            }));
        }, []);

        const updateFormData = useCallback((updates: Partial<VehicleOwnerFormData>) => {
            setFormData(prev => ({ ...prev, ...updates }));
        }, []);

        const resetForm = useCallback(() => {
            setFormData({
                ...initialData,
                ownerAddress: initialData.ownerAddress ? { ...initialData.ownerAddress } : null,
                payerAddress: initialData.payerAddress ? { ...initialData.payerAddress } : null,
            });
        }, [initialData]);

        const ownerFormFields = {
            ownerFormData: formData,
            handleInputField,
            handlePhoneNumberField,
            handleInputChange,
            onChangeOwnerAddress,
            onChangePayerAddress,
            updateFormData,
            resetForm,
            setOwnerFormData: setFormData,
        };

        return <VehicleOwnerStep ownerFormFields={ownerFormFields} formState={formState} isCompany={isCompany} />;
    },
);

EditableVehicleOwner.displayName = 'EditableVehicleOwner';
